import { useEffect } from "react";
import { can } from "modules/session/auth/access";
import func from "../../functionalities";
import { FinanceRequestsStoreType } from "../models/finance-requests-store";

/** Фин. запросы старше этого перечитываются, когда сотрудник возвращается на вкладку браузера */
const MAX_AGE = 5 * 60 * 1000;

/**
 * Счётчик фин. запросов для пункта меню: сколько ждёт одобрения и оплаты.
 *
 * Правило то же, что в PWA: новые запросы считаются при праве одобрения, одобренные —
 * при праве оплаты. Без обоих прав список не запрашивается совсем — сервер ответит отказом.
 * Список грузится при входе и перечитывается при возврате на вкладку браузера, если устарел;
 * после одобрения или отмены его и так перезагружает стор. Загрузка тихая: сбой фоновой
 * загрузки не выводит ошибку, которую человек не вызывал, — счётчик остаётся прежним.
 *
 * Возвращает строку для `pin1`: пустую при нуле (счётчик не рисуется), «99+» сверх 99.
 * Вызывать из `observer`-компонента: число читается из стора на рендере.
 */
export function useFinanceRequestsCount(store: FinanceRequestsStoreType, access: string[]) {
    const canAccept = can(func.FINANCE_REQUEST_ACCEPT, access);
    const canPay = can(func.FINANCE_REQUEST_PAY, access);
    const visible = canAccept || canPay;

    useEffect(() => {
        if (!visible) {
            return;
        }

        store.load(true);

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                store.loadIfStale(MAX_AGE);
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [store, visible]);

    const count = store.pendingCount(canAccept, canPay);
    if (count === 0) {
        return "";
    }

    return count > 99 ? "99+" : `${count}`;
}
